import {
    Avatar, Box, CircularProgress, IconButton, InputAdornment,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { HomeWorkOutlined, Refresh, Search, VisibilityOutlined } from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import _ from 'lodash'
import LandlordPropertiesModal from "./propertiesModal"

interface LandlordRow {
    userId: string
    name: string
    email?: string
    phone?: string
    profilePhotos?: any
    totalProperties: number
}

interface StateProps {
    landlords: LandlordRow[]
    loading: boolean
    refreshing: boolean
    searchVal: string
    page: number
    pageSize: number
    count: number
    viewingLandlord: LandlordRow | null
}

const AdminLandlords: FC = () => {
    const [state, setState] = useState<StateProps>({
        landlords: [],
        loading: true,
        refreshing: false,
        searchVal: '',
        page: 0,
        pageSize: 25,
        count: 0,
        viewingLandlord: null
    })

    const { landlords, loading, refreshing, searchVal, page, pageSize, count, viewingLandlord } = state

    const fetchLandlords = ({ searchVal: sv, newPage, newPageSize, processVariable }: any) => {
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        const Search = sv !== undefined ? sv : state.searchVal
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, page: Page, pageSize: PageSize, searchVal: Search }
        Apis({ module: 'Properties', action: 'getLandlords', searchVal: Search, page: Page, pageSize: PageSize })
            .then((resp: any) => setState(s => ({ ...s, landlords: resp.landlords || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const startSearch = useCallback(_.debounce((val: string) => fetchLandlords({ searchVal: val, newPage: 0, processVariable: 'loading' }), 600), [])

    useEffect(() => {
        fetchLandlords({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Landlords" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {count.toLocaleString()} total
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        variant="outlined"
                        onChange={e => startSearch(e.target.value)}
                        sx={{ width: 240, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchLandlords({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!landlords.length || loading ?
                <NoData message="No landlords found." loading={loading} searchVal={searchVal} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Phone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Properties</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {landlords.map(landlord => (
                                    <TableRow hover key={landlord.userId}>
                                        <TableCell>
                                            <Avatar src={landlord.profilePhotos?.small?.url} sx={{ width: 28, height: 28, fontSize: 12 }} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{landlord.name?.trim() || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{landlord.email || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{landlord.phone || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                <HomeWorkOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                {landlord.totalProperties}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View properties">
                                                <IconButton size="small" onClick={() => setState(s => ({ ...s, viewingLandlord: landlord }))}>
                                                    <VisibilityOutlined sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchLandlords({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchLandlords({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }

            {viewingLandlord && (
                <LandlordPropertiesModal
                    landlordUserId={viewingLandlord.userId}
                    landlordName={viewingLandlord.name?.trim() || 'Landlord'}
                    close={() => setState(s => ({ ...s, viewingLandlord: null }))}
                />
            )}
        </Box>
    )
}

export default AdminLandlords
